import { type Draft } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  applyPropsPatch,
  type CanvasElementId,
  type ColumnId,
  getFormGroupLikeWrapperMainFormId,
  type PropsOf,
  setWrapperRefMutably,
  WrapperForm,
  type WrapperRefs,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDataProperties,
  type DataBindingSourceInfo,
  FORM_GROUP_ACTION_MAPPING_MAIN,
} from '@blaze-shared/elements-domain';
import { type StyleSheet } from '@blaze-shared/theming-domain';
import { type FormActionType } from '@blaze-shared/workflow-domain';

import { type CanvasElementDefinitionInternal } from '../../../canvas-elements/element-definition';
import {
  isElementType,
  isVariantOfFormContainer,
} from '../../../canvas-elements/element-model/element-type-guards';
import { getOrCreateWrapperRefsMutably } from '../../../canvas-elements/element-model/wrapper-refs';
import { getWizardSteps } from '../../../elements/wizard/wizard.utils';
import { getPathLastInSlot } from '../../../utils/layout/helpers';
import { placeElementMutably } from '../../../utils/layout/place-element';

/** Everything placing a questionnaire's question forms needs beyond the draft itself. */
export interface QuestionFormsData {
  /** The dataset every question form is generated from, with its columns already loaded. */
  dataSource: DataBindingSourceInfo;
  styleSheets: StyleSheet[];
  formContainerDefinition: CanvasElementDefinitionInternal;
}

/** One question's form, paired with the step it belongs to. */
export interface QuestionForm {
  stepIndex: number;
  slotId: ColumnId;
  columnKey: string;
  formId: CanvasElementId;
  /** Whether this form holds the wrapper's main-form ref — see {@link applyQuestionFormsMutably}. */
  isMain: boolean;
  /**
   * Whether this pass placed the form, rather than finding it already in the slot. A form
   * being born is the only moment its fields can be generated from scratch without
   * overruling what the builder has since configured in its panel.
   */
  isNewlyPlaced: boolean;
}

/**
 * Gives every question step a one-field form in its own slot, and keys the wizard's wrapper
 * refs to match.
 *
 * **A question form's identity is its slot.** Every form is keyed `form-<datasetId>--<slotId>`,
 * which rides on the ref format `getFormGroupLikeWrapper` already parses
 * (`form-<datasetId>-<keyColumnId>-<index>`) with an empty key-column segment — so the ~10
 * readers of that helper keep working unchanged, and only the allocator of the last segment is
 * new. Dataset ids are hex (a column id is `<24 hex>_<24 hex>`), so they never contain the `-`
 * that separates the segments.
 *
 * **One form additionally holds the main-form ref** `form-<datasetId>`, because
 * `createOrUpdateFormGroupLikeSubmitWorkflowAndConnections` generates no submit workflow at all
 * without one. That is a role, not a kind: every question form is generated identically. The
 * role stays with whichever form already holds it as long as its step survives, so reordering
 * questions never re-points the submit workflow's main input; it moves to the first question's
 * form only when the incumbent's question is deleted.
 *
 * @returns every question form in step order, so the caller can generate each one's field
 */
export function applyQuestionFormsMutably(
  draft: Draft<EngineStateBase>,
  wizardId: CanvasElementId,
  { dataSource, styleSheets, formContainerDefinition }: QuestionFormsData,
): QuestionForm[] {
  const wizard = draft.entities[wizardId];
  if (!isElementType(wizard, 'wizard')) {
    throw new Error('element is not a wizard');
  }

  const refs = getOrCreateWrapperRefsMutably(wizard);
  const datasetId = dataSource.id;
  const action =
    wizard.props.content.action && FORM_GROUP_ACTION_MAPPING_MAIN[wizard.props.content.action];

  // The form already sitting in a step's slot is that step's form, whatever it used to be keyed
  // as — the slot is the identity, so a re-keyed or promoted form is found here either way.
  const forms: QuestionForm[] = [];
  getWizardSteps(wizard.props).forEach(({ columnKey, slotId }, stepIndex) => {
    if (!columnKey || !slotId) {
      return;
    }
    const existingFormId = findFormInSlot(draft, wizardId, slotId);
    forms.push({
      stepIndex,
      slotId,
      columnKey,
      formId:
        existingFormId ??
        placeQuestionFormMutably(draft, wizardId, slotId, {
          action,
          dataSource,
          styleSheets,
          formContainerDefinition,
        }),
      isMain: false,
      isNewlyPlaced: !existingFormId,
    });
  });

  const mainFormId = electMainForm(refs, forms);
  rekeyFormRefsMutably(refs, datasetId, forms, mainFormId);

  return forms.map((form) => ({ ...form, isMain: form.formId === mainFormId }));
}

/** The form element a slot already holds, if the slot has one. */
function findFormInSlot(
  draft: Draft<EngineStateBase>,
  wizardId: CanvasElementId,
  slotId: ColumnId,
): CanvasElementId | undefined {
  const wizard = draft.entities[wizardId];
  if (!isElementType(wizard, 'wizard')) {
    return undefined;
  }

  return (wizard.slots[slotId]?.children ?? []).find((childId) => {
    const child = draft.entities[childId];
    return isElementType(child, 'formContainer') || isElementType(child, 'formDetails');
  });
}

/**
 * Places a question's form in its slot, bound to the questionnaire's dataset. The fields
 * themselves are generated afterwards, by the form generation the caller drives.
 */
function placeQuestionFormMutably(
  draft: Draft<EngineStateBase>,
  wizardId: CanvasElementId,
  slotId: ColumnId,
  {
    action,
    dataSource,
    styleSheets,
    formContainerDefinition,
  }: QuestionFormsData & { action: FormActionType | undefined },
): CanvasElementId {
  const dataSourceValue: CanvasElementDataProperties = {
    key: 'value',
    dataSource: { ...dataSource, sourceType: 'formGroup' },
    enabled: true,
  };

  const placed = placeElementMutably(draft, {
    elementDefinition: {
      ...formContainerDefinition,
      height: { mode: 'auto' },
      width: { mode: 'full' },
      ...(action === 'view' ? { type: 'formDetails' } : {}),
    },
    atPath: getPathLastInSlot(draft, wizardId, slotId),
    initProps: {
      data: [dataSourceValue],
      content: {
        action,
        origin: 'wizard',
        options: { markRequiredFields: true },
      },
    },
    style: { styleSheets, useDefault: true },
  });

  if (!placed?.elementId) {
    throw new Error('unable to place question form');
  }

  const placedForm = draft.entities[placed.elementId];
  if (!isVariantOfFormContainer(placedForm)) {
    throw new Error('unexpected question form element type');
  }
  // The question form's spacing is intentional and must survive the style-path cleanup
  // performed by the default-style assignment
  placedForm.props = applyPropsPatch(placedForm.props, {
    style: {
      container: {
        // a question form is the whole of its step, so it carries no outer spacing of its own
        offset: { margin: '0px', padding: '$t.spacingFormGroupForm' },
      },
    },
  }) as PropsOf<'formContainer' | 'formDetails'>;

  return placed.elementId;
}

/**
 * Which form carries the main-form ref: the one that already does, as long as it is still a
 * question's form, otherwise the first question's. Preferring the incumbent is what keeps a
 * reorder from re-pointing the submit workflow.
 */
function electMainForm(
  refs: WrapperRefs,
  forms: { formId: CanvasElementId }[],
): CanvasElementId | undefined {
  const { mainFormId: incumbentId } = getFormGroupLikeWrapperMainFormId(refs);
  const incumbent = forms.find(({ formId }) => formId === incumbentId);

  return (incumbent ?? forms[0])?.formId;
}

/**
 * Rewrites every `form-*` ref from scratch, so refs left by removed questions, by a previous
 * dataset, or by a form that has just changed role cannot survive the pass.
 */
function rekeyFormRefsMutably(
  refs: WrapperRefs,
  datasetId: string,
  forms: { formId: CanvasElementId; slotId: ColumnId }[],
  mainFormId: CanvasElementId | undefined,
): void {
  for (const refId of Object.keys(refs)) {
    if (WrapperForm.isRefId(refId)) {
      delete refs[refId];
    }
  }

  for (const { formId, slotId } of forms) {
    const refValue = formId === mainFormId ? datasetId : `${datasetId}--${slotId}`;
    setWrapperRefMutably(refs, WrapperForm.getRefIds(undefined, refValue), formId);
  }
}
